import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface InventoryCostingPolicy {
  id: string;
  tenantId: string;
  offeringId: string | null;
  warehouseId: string | null;
  method: string;
  effectiveFromUtc: string;
  currencyCode: string;
  roundingScale: number;
  hardBlockNegativeStock: boolean;
  isActive: boolean;
  specificityScore: number;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface UpsertInventoryCostingPolicyPayload {
  offeringId: string | null;
  warehouseId: string | null;
  method: string;
  effectiveFromUtc: string;
  currencyCode: string;
  roundingScale: number;
  hardBlockNegativeStock: boolean;
  isActive?: boolean;
}

export interface InventoryCostingPolicyChangeAudit {
  id: string;
  inventoryCostingPolicyId: string;
  previousMethod: string;
  newMethod: string;
  previousIsActive: boolean;
  newIsActive: boolean;
  reason: string;
  changedAtUtc: string;
}

export interface InventoryExpiryStatusLine {
  lotId: string;
  offeringId: string;
  warehouseId: string;
  lotNumber: string;
  quantity: number;
  expiryDate: string | null;
  lotStatus: string;
  category: string;
}

export interface InventoryExpiryStatusReport {
  asOfDate: string;
  nearExpiryDays: number;
  expiredCount: number;
  nearExpiryCount: number;
  blockedCount: number;
  lines: InventoryExpiryStatusLine[];
}

export interface InventoryTransferPrintDocument {
  transferId: string;
  transferReferenceNumber: string;
  issueReferenceNumber: string;
  receiptReferenceNumber: string;
  offeringId: string;
  quantity: number;
  fromWarehouseId: string;
  toWarehouseId: string;
  processedAtUtc: string;
}

export interface InventoryCostLayer {
  id: string;
  tenantId: string;
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  lotId: string | null;
  originalQuantity: number;
  remainingQuantity: number;
  unitCost: number;
  currencyCode: string;
  receivedAtUtc: string;
  sourceCostLedgerEntryId: string | null;
}

export interface InventoryCostValuationLine {
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  lotId: string | null;
  quantity: number;
  totalCost: number;
  averageUnitCost: number;
  currencyCode: string;
}

export interface InventoryCostValuation {
  currencyCode: string;
  totalQuantity: number;
  totalCost: number;
  lines: InventoryCostValuationLine[];
}

export interface InventoryValuationHistoryItem {
  runId: string;
  snapshotId: string;
  referenceNumber: string;
  runAtUtc: string;
  triggerSource: string;
  currencyCode: string;
  totalQuantity: number;
  totalCost: number;
}

export interface InventoryValuationSnapshotLine {
  id: string;
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  lotId: string | null;
  quantity: number;
  totalCost: number;
  averageUnitCost: number;
  currencyCode: string;
}

export interface InventoryValuationSnapshot {
  id: string;
  tenantId: string;
  capturedAtUtc: string;
  currencyCode: string;
  totalQuantity: number;
  totalCost: number;
  lines: InventoryValuationSnapshotLine[];
}

export interface CaptureInventoryValuationPayload {
  referenceNumber: string;
  triggerSource?: string | null;
  offeringId?: string | null;
  warehouseId?: string | null;
}

export interface InventoryCostRevaluationLine {
  id: string;
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  lotId: string | null;
  deltaValue: number;
}

export interface InventoryCostRevaluation {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  referenceNumber: string;
  status: string;
  reason: string;
  currencyCode: string;
  postIdempotencyKey: string | null;
  effectiveAtUtc: string;
  postedAtUtc: string | null;
  createdAtUtc: string;
  lines: InventoryCostRevaluationLine[];
}

export interface CreateInventoryCostRevaluationLinePayload {
  offeringId: string;
  warehouseId: string;
  binId?: string | null;
  lotId?: string | null;
  deltaValue: number;
}

export interface CreateInventoryCostRevaluationPayload {
  referenceNumber: string;
  reason: string;
  currencyCode: string;
  effectiveAtUtc?: string | null;
  lines: CreateInventoryCostRevaluationLinePayload[];
}

export interface PostInventoryCostRevaluationPayload {
  idempotencyKey: string;
}

export interface LandedCostAllocationLine {
  id: string;
  inboundShipmentLineId: string;
  offeringId: string;
  receivedQuantity: number;
  receiptValue: number;
  allocationWeight: number | null;
  allocatedAmount: number;
}

export interface LandedCostAllocation {
  id: string;
  tenantId: string;
  organizationUnitId: string | null;
  inboundShipmentId: string;
  referenceNumber: string;
  status: string;
  allocationMethod: string;
  totalLandedCostAmount: number;
  currencyCode: string;
  reason: string | null;
  commitIdempotencyKey: string | null;
  committedAtUtc: string | null;
  createdAtUtc: string;
  lines: LandedCostAllocationLine[];
}

export interface CreateLandedCostAllocationPayload {
  referenceNumber: string;
  allocationMethod: string;
  totalLandedCostAmount: number;
  currencyCode: string;
  reason?: string | null;
  lineWeights?: { inboundShipmentLineId: string; weight: number }[] | null;
}

export interface PreviewLandedCostAllocationPayload {
  allocationMethod?: string | null;
  totalLandedCostAmount?: number | null;
  reason?: string | null;
  lineWeights?: { inboundShipmentLineId: string; weight: number }[] | null;
}

export interface CommitLandedCostAllocationPayload {
  idempotencyKey: string;
  effectiveAtUtc?: string | null;
}

@Injectable({ providedIn: 'root' })
export class InventoryCostingApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = environment.apiBaseUrl;

  listPolicies(filters?: { offeringId?: string; warehouseId?: string; isActive?: boolean }): Observable<InventoryCostingPolicy[]> {
    let params = new HttpParams();
    if (filters?.offeringId) params = params.set('offeringId', filters.offeringId);
    if (filters?.warehouseId) params = params.set('warehouseId', filters.warehouseId);
    if (filters?.isActive !== undefined) params = params.set('isActive', String(filters.isActive));
    return this.http.get<InventoryCostingPolicy[]>(`${this.apiBase}/api/v1/inventory/costing/policies`, { params });
  }

  getPolicy(id: string): Observable<InventoryCostingPolicy> {
    return this.http.get<InventoryCostingPolicy>(`${this.apiBase}/api/v1/inventory/costing/policies/${id}`);
  }

  createPolicy(payload: UpsertInventoryCostingPolicyPayload): Observable<InventoryCostingPolicy> {
    return this.http.post<InventoryCostingPolicy>(`${this.apiBase}/api/v1/inventory/costing/policies`, payload);
  }

  updatePolicy(id: string, payload: UpsertInventoryCostingPolicyPayload): Observable<InventoryCostingPolicy> {
    return this.http.put<InventoryCostingPolicy>(`${this.apiBase}/api/v1/inventory/costing/policies/${id}`, payload);
  }

  changePolicyWithAudit(id: string, payload: UpsertInventoryCostingPolicyPayload & { reason: string }): Observable<InventoryCostingPolicy> {
    return this.http.put<InventoryCostingPolicy>(`${this.apiBase}/api/v1/inventory/costing/policies/${id}/change`, payload);
  }

  bootstrapDefaultPolicy(currencyCode?: string): Observable<{ policyId: string | null; method: string; currencyCode: string; created: boolean }> {
    return this.http.post<{ policyId: string | null; method: string; currencyCode: string; created: boolean }>(
      `${this.apiBase}/api/v1/inventory/costing/policies/bootstrap-default`,
      { currencyCode: currencyCode ?? null });
  }

  listPolicyChangeAudits(id: string): Observable<InventoryCostingPolicyChangeAudit[]> {
    return this.http.get<InventoryCostingPolicyChangeAudit[]>(`${this.apiBase}/api/v1/inventory/costing/policies/${id}/change-audits`);
  }

  getExpiryStatusReport(filters?: { warehouseId?: string; offeringId?: string; nearExpiryDays?: number; category?: string }): Observable<InventoryExpiryStatusReport> {
    let params = new HttpParams();
    if (filters?.warehouseId) params = params.set('warehouseId', filters.warehouseId);
    if (filters?.offeringId) params = params.set('offeringId', filters.offeringId);
    if (filters?.nearExpiryDays !== undefined) params = params.set('nearExpiryDays', String(filters.nearExpiryDays));
    if (filters?.category) params = params.set('category', filters.category);
    return this.http.get<InventoryExpiryStatusReport>(`${this.apiBase}/api/v1/inventory/reports/expiry-status`, { params });
  }

  getTransferPrintDocument(movementId: string): Observable<InventoryTransferPrintDocument> {
    return this.http.get<InventoryTransferPrintDocument>(`${this.apiBase}/api/v1/inventory/movements/${movementId}/transfer-print`);
  }

  listCostLayers(filters?: { offeringId?: string; warehouseId?: string; openOnly?: boolean }): Observable<InventoryCostLayer[]> {
    let params = new HttpParams();
    if (filters?.offeringId) params = params.set('offeringId', filters.offeringId);
    if (filters?.warehouseId) params = params.set('warehouseId', filters.warehouseId);
    if (filters?.openOnly) params = params.set('openOnly', 'true');
    return this.http.get<InventoryCostLayer[]>(`${this.apiBase}/api/v1/inventory/costing/layers`, { params });
  }

  getCostLayer(id: string): Observable<InventoryCostLayer> {
    return this.http.get<InventoryCostLayer>(`${this.apiBase}/api/v1/inventory/costing/layers/${id}`);
  }

  getValuation(filters?: { offeringId?: string; warehouseId?: string; currencyCode?: string }): Observable<InventoryCostValuation> {
    let params = new HttpParams();
    if (filters?.offeringId) params = params.set('offeringId', filters.offeringId);
    if (filters?.warehouseId) params = params.set('warehouseId', filters.warehouseId);
    if (filters?.currencyCode) params = params.set('currencyCode', filters.currencyCode);
    return this.http.get<InventoryCostValuation>(`${this.apiBase}/api/v1/inventory/costing/valuation`, { params });
  }

  listValuationHistory(): Observable<InventoryValuationHistoryItem[]> {
    return this.http.get<InventoryValuationHistoryItem[]>(`${this.apiBase}/api/v1/inventory/costing/valuation-history`);
  }

  getValuationSnapshot(id: string): Observable<InventoryValuationSnapshot> {
    return this.http.get<InventoryValuationSnapshot>(`${this.apiBase}/api/v1/inventory/costing/valuation-snapshots/${id}`);
  }

  captureValuationRun(payload: CaptureInventoryValuationPayload): Observable<{ run: { id: string }; snapshot: InventoryValuationSnapshot }> {
    return this.http.post<{ run: { id: string }; snapshot: InventoryValuationSnapshot }>(
      `${this.apiBase}/api/v1/inventory/costing/valuation-runs`,
      payload
    );
  }

  listRevaluations(status?: string): Observable<InventoryCostRevaluation[]> {
    const params = status ? new HttpParams().set('status', status) : undefined;
    return this.http.get<InventoryCostRevaluation[]>(`${this.apiBase}/api/v1/inventory/costing/revaluations`, { params });
  }

  getRevaluation(id: string): Observable<InventoryCostRevaluation> {
    return this.http.get<InventoryCostRevaluation>(`${this.apiBase}/api/v1/inventory/costing/revaluations/${id}`);
  }

  createRevaluation(payload: CreateInventoryCostRevaluationPayload): Observable<InventoryCostRevaluation> {
    return this.http.post<InventoryCostRevaluation>(`${this.apiBase}/api/v1/inventory/costing/revaluations`, payload);
  }

  approveRevaluation(id: string): Observable<InventoryCostRevaluation> {
    return this.http.post<InventoryCostRevaluation>(`${this.apiBase}/api/v1/inventory/costing/revaluations/${id}/approve`, {});
  }

  postRevaluation(id: string, payload: PostInventoryCostRevaluationPayload): Observable<InventoryCostRevaluation> {
    return this.http.post<InventoryCostRevaluation>(`${this.apiBase}/api/v1/inventory/costing/revaluations/${id}/post`, payload);
  }

  cancelRevaluation(id: string): Observable<InventoryCostRevaluation> {
    return this.http.post<InventoryCostRevaluation>(`${this.apiBase}/api/v1/inventory/costing/revaluations/${id}/cancel`, {});
  }

  listLandedCostAllocationsByShipment(shipmentId: string): Observable<LandedCostAllocation[]> {
    return this.http.get<LandedCostAllocation[]>(
      `${this.apiBase}/api/v1/inventory/inbound-shipments/${shipmentId}/landed-cost-allocations`
    );
  }

  getLandedCostAllocation(id: string): Observable<LandedCostAllocation> {
    return this.http.get<LandedCostAllocation>(`${this.apiBase}/api/v1/inventory/costing/landed-cost-allocations/${id}`);
  }

  createLandedCostAllocation(shipmentId: string, payload: CreateLandedCostAllocationPayload): Observable<LandedCostAllocation> {
    return this.http.post<LandedCostAllocation>(
      `${this.apiBase}/api/v1/inventory/inbound-shipments/${shipmentId}/landed-cost-allocations`,
      payload
    );
  }

  previewLandedCostAllocation(id: string, payload?: PreviewLandedCostAllocationPayload): Observable<LandedCostAllocation> {
    return this.http.post<LandedCostAllocation>(
      `${this.apiBase}/api/v1/inventory/costing/landed-cost-allocations/${id}/preview`,
      payload ?? {}
    );
  }

  commitLandedCostAllocation(id: string, payload: CommitLandedCostAllocationPayload): Observable<LandedCostAllocation> {
    return this.http.post<LandedCostAllocation>(
      `${this.apiBase}/api/v1/inventory/costing/landed-cost-allocations/${id}/commit`,
      payload
    );
  }

  cancelLandedCostAllocation(id: string): Observable<LandedCostAllocation> {
    return this.http.post<LandedCostAllocation>(
      `${this.apiBase}/api/v1/inventory/costing/landed-cost-allocations/${id}/cancel`,
      {}
    );
  }

  getGlPostingOutboxSummary(): Observable<InventoryGlPostingOutboxSummary> {
    return this.http.get<InventoryGlPostingOutboxSummary>(`${this.apiBase}/api/v1/inventory/operability/outbox-summary`);
  }

  runReconciliation(filters?: {
    offeringId?: string;
    warehouseId?: string;
    movementId?: string;
  }): Observable<InventoryReconciliationReport> {
    let params = new HttpParams();
    if (filters?.offeringId) params = params.set('offeringId', filters.offeringId);
    if (filters?.warehouseId) params = params.set('warehouseId', filters.warehouseId);
    if (filters?.movementId) params = params.set('movementId', filters.movementId);
    return this.http.get<InventoryReconciliationReport>(`${this.apiBase}/api/v1/inventory/reconciliation`, { params });
  }

  exportReconciliation(filters?: {
    offeringId?: string;
    warehouseId?: string;
    movementId?: string;
  }): Observable<string> {
    let params = new HttpParams();
    if (filters?.offeringId) params = params.set('offeringId', filters.offeringId);
    if (filters?.warehouseId) params = params.set('warehouseId', filters.warehouseId);
    if (filters?.movementId) params = params.set('movementId', filters.movementId);
    return this.http.get(`${this.apiBase}/api/v1/inventory/reconciliation/export`, { params, responseType: 'text' });
  }

  getAuditTrail(filters?: {
    movementId?: string;
    category?: string;
    fromUtc?: string;
    toUtc?: string;
    page?: number;
    pageSize?: number;
  }): Observable<InventoryAuditTrailPage> {
    let params = new HttpParams();
    if (filters?.movementId) params = params.set('movementId', filters.movementId);
    if (filters?.category) params = params.set('category', filters.category);
    if (filters?.fromUtc) params = params.set('fromUtc', filters.fromUtc);
    if (filters?.toUtc) params = params.set('toUtc', filters.toUtc);
    if (filters?.page) params = params.set('page', String(filters.page));
    if (filters?.pageSize) params = params.set('pageSize', String(filters.pageSize));
    return this.http.get<InventoryAuditTrailPage>(`${this.apiBase}/api/v1/inventory/audit-trail`, { params });
  }
}

export interface InventoryGlPostingOutboxSummary {
  tenantId: string;
  pendingCount: number;
  failedCount: number;
  processingCount: number;
  agedPendingCount: number;
  stuckProcessingCount: number;
  retryScheduledCount: number;
  requiresAttention: boolean;
}

export interface InventoryReconciliationReport {
  tenantId: string;
  generatedAtUtc: string;
  stockQuantityDrifts: InventoryStockQuantityDrift[];
  costDrifts: InventoryCostDrift[];
  outboxPostingDrifts: InventoryOutboxPostingDrift[];
  fxDrifts: InventoryFxDrift[];
  isBalanced: boolean;
}

export interface InventoryStockQuantityDrift {
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  balanceQuantity: number;
  ledgerQuantity: number;
  delta: number;
}

export interface InventoryCostDrift {
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  lotId: string | null;
  balanceQuantity: number;
  ledgerQuantity: number;
  quantityDelta: number;
  balanceTotalCost: number;
  ledgerTotalCost: number;
  totalCostDelta: number;
  layerQuantity: number;
  layerTotalCost: number;
  layerQuantityDelta: number;
  layerCostDelta: number;
}

export interface InventoryOutboxPostingDrift {
  outboxId: string;
  movementId: string | null;
  eventType: string;
  outboxStatus: string;
  idempotencyKey: string;
  postingStatus: string | null;
  driftReason: string;
}

export interface InventoryFxDrift {
  offeringId: string;
  warehouseId: string;
  binId: string | null;
  lotId: string | null;
  currencyCode: string;
  balanceTotalCost: number;
  ledgerTotalCost: number;
  delta: number;
  driftReason: string;
}

export interface InventoryAuditTrailPage {
  page: number;
  pageSize: number;
  totalCount: number;
  items: InventoryAuditTrailEntry[];
}

export interface InventoryAuditTrailEntry {
  category: string;
  entityId: string;
  movementId: string | null;
  referenceNumber: string;
  operationalStatus: string;
  postingStatus: string | null;
  postingErrorCode: string | null;
  journalEntryId: string | null;
  amount: number | null;
  currencyCode: string | null;
  eventType: string | null;
  idempotencyKey: string | null;
  occurredAtUtc: string;
  postingEvidenceStatus: string | null;
  postingEvidenceJournalEntryId: string | null;
  postingEvidenceErrorCode: string | null;
  postingEvidenceAmount: number | null;
  postingEvidenceCurrencyCode: string | null;
}
